import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

interface ArmorDisplayProps {
  frontArmor: number;
  sideArmor: number;
  rearArmor: number;
  topArmor: number;
  frontArmorType: string;
  sideArmorType: string;
  rearArmorType: string;
  topArmorType: string;
  era: boolean;
  className?: string;
  /** Applied to the inner 3×3 grid (default caps width for compact layouts) */
  gridClassName?: string;
}

function ArmourFaceCell({
  label,
  value,
  armourType,
}: {
  label: string;
  value: number;
  armourType: string;
}) {
  return (
    <div className="flex h-16 flex-col items-center justify-center rounded-md border border-border bg-muted/30 px-1 text-center">
      <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground leading-tight">
        {label}
      </div>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="text-xl font-bold cursor-help tabular-nums leading-tight">{value}</div>
        </TooltipTrigger>
        <TooltipContent>
          <p>{armourType}</p>
        </TooltipContent>
      </Tooltip>
    </div>
  );
}

/** Single facing chip for narrow screens (saves vertical space vs 3×3 cross) */
function ArmourFaceChip({
  label,
  value,
  armourType,
}: {
  label: string;
  value: number;
  armourType: string;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="flex cursor-help items-center gap-1.5 rounded-md border border-border bg-muted/30 px-2 py-1.5">
          <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
            {label}
          </span>
          <span className="text-sm font-bold tabular-nums leading-none">{value}</span>
        </div>
      </TooltipTrigger>
      <TooltipContent>
        <p>{armourType}</p>
      </TooltipContent>
    </Tooltip>
  );
}

function CornerSpacer() {
  return <div className="h-16 min-h-16" aria-hidden />;
}

export function ArmorDisplay({
  frontArmor,
  sideArmor,
  rearArmor,
  topArmor,
  frontArmorType,
  sideArmorType,
  rearArmorType,
  topArmorType,
  era,
  className,
  gridClassName,
}: ArmorDisplayProps) {
  const eraBadge = era ? (
    <div className="mt-2">
      <Badge variant="secondary" className="text-xs">
        ERA (Explosive Reactive Armor)
      </Badge>
    </div>
  ) : null;

  return (
    <div className={cn("mt-2 pt-2 border-t", className)}>
      {/* Compact wrapping strip: phones / narrow tablets */}
      <div className="md:hidden">
        <div className="flex flex-wrap gap-1.5" aria-label="Armour by facing">
          <ArmourFaceChip label="Front" value={frontArmor} armourType={frontArmorType} />
          <ArmourFaceChip label="Side" value={sideArmor} armourType={sideArmorType} />
          <ArmourFaceChip label="Top" value={topArmor} armourType={topArmorType} />
          <ArmourFaceChip label="Rear" value={rearArmor} armourType={rearArmorType} />
        </div>
        {eraBadge}
      </div>

      {/* Facing cross: md and up */}
      <div className="hidden md:block">
        <div
          className={cn(
            "grid grid-cols-3 gap-1.5",
            gridClassName ?? "max-w-[13.5rem]",
          )}
        >
          <CornerSpacer />
          <ArmourFaceCell label="Front" value={frontArmor} armourType={frontArmorType} />
          <CornerSpacer />
          <ArmourFaceCell label="Side" value={sideArmor} armourType={sideArmorType} />
          <ArmourFaceCell label="Top" value={topArmor} armourType={topArmorType} />
          <ArmourFaceCell label="Side" value={sideArmor} armourType={sideArmorType} />
          <CornerSpacer />
          <ArmourFaceCell label="Rear" value={rearArmor} armourType={rearArmorType} />
          <CornerSpacer />
        </div>
        {eraBadge}
      </div>
    </div>
  );
}
